import UserProfileDetailsCard from 'components/Layout/UserProfileDetailsCard';
import DetailItem from 'components/Layout/DetailItem';
import Card from 'components/Layout/Card';
import ActionForm from '../../Common/ActionForm';

import {
  getDataFromLocalStorage,
  getFormDataArrayObject,
  getStatus,
  getStatusColor,
  handleFileDownload,
  objectToFormData,
} from 'utils/helpers';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchMembershipRequestDetails,
  handleSendMembershipTypeChangeOtp,
  handleVerifyMembershipTypeChangeOtpRequest,
  setIsEditDataOfProfile,
  setIsEditUserRequestProfile,
  showSuccess,
  throwError,
} from 'store/slices';
import RequestVerificationModal from '../../Common/RequestVerificationModal';
import VerifyOtpModel from '../../Common/VerifyOtpModel';
import moment from 'moment';
import { CVPath, icons, paymentAbstractPath, requestDataPath } from 'utils/constants';
import EducationSection from './EducationSection';
import RequestSection from '../../Common/RequestSection/RequestSection';
import Loader from 'components/Layout/Loader';
import Button from 'components/form/Button';
import UpdateUserDetailsPopup from 'components/ReusableForms/UpdateUserDetailsPopup';
import usePermission from 'hooks/usePermission';

const MembershipTypeChangeDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { updateAccess } = usePermission("Requests");
  const { requestId } = useParams();
  const user_type = getDataFromLocalStorage('user_type');
  const [isRejecting, setIsRejecting] = useState(false);
  const [otpData, setOtpData] = useState({});
  const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] =
    useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [otpVerification, setOtpVerification] = useState(false);

  const reduxData = useSelector((state) => state.global);
  const { isEditUserRequestProfile } = reduxData || {};

  // Formik setup
  const initialValues = {
    comment: '',
    document: '',
  };
  const [requestDetails, setRequestDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const getMembershipRequestDetails = async () => {
    const response = await dispatch(fetchMembershipRequestDetails(requestId));
    if (response.status === 200) {
      setRequestDetails(response?.data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (['0', '1', '6', '7'].includes(getDataFromLocalStorage('user_type'))) {
      getMembershipRequestDetails();
    } else {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const handleReject = async (values, setErrors) => {
    setIsRejecting(true);
    if (!values.comment.trim() && !values.document) {
      setErrors({
        comment: 'Either a comment or a document is required',
        document: 'Either a comment or a document is required',
      });
      return;
    }
    setOtpData({
      comment: values?.comment,
      document: values?.document,
      action: '2', // Reject
    });
    await sendOtpRequest();
  };

  const handleApproveRequest = async (values, setErrors) => {
    setOtpData({
      comment: values?.comment,
      document: values?.document,
      action: '1', // Approve
    });
    if (user_type === '1') {
      await sendOtpRequest();
    } else {
      setIsOpenConfirmationDialog(true);
    }
  };

  const sendOtpRequest = async () => {
    setBtnLoading(true);
    const payload = objectToFormData({ request_id: requestId });
    const response = await dispatch(handleSendMembershipTypeChangeOtp(payload));
    if (response.status === 200) {
      setShowOtpDialog(true);
      dispatch(showSuccess(response?.message));
      setBtnLoading(false);
    } else {
      dispatch(throwError(response));
      setBtnLoading(false);
    }
  };

  const handleVerifyOtp = async (otp) => {
    setOtpVerification(true);
    const payload = getFormDataArrayObject({
      request_id: requestId,
      ...(otp && { otp }),
      document: otpData?.document,
      comment: otpData?.comment,
      request_status: otpData?.action,
      request_data: 1,
    });
    const response = await dispatch(
      handleVerifyMembershipTypeChangeOtpRequest(payload)
    );
    if (response?.status === 200) {
      setShowOtpDialog(false);
      dispatch(showSuccess(response?.message));
      setIsOpenConfirmationDialog(false);
      setOtpVerification(false);
      getMembershipRequestDetails();
    } else {
      dispatch(throwError(response));
      setOtpVerification(false);
    }
  };

  const {
    user_details,
    educational_details,
    resource_details,
    admin_details,
    sadmin_details,
  } = requestDetails;

  // User type and status
  const isAdmin = user_type === '0';
  const isSuperAdmin = user_type === '1';
  const isActionTakenByAdmin = admin_details?.admin_status !== 0;
  const isActionTakenBySuperAdmin = sadmin_details?.sadmin_status !== 0;

  return (
    <div id="membershiptypechange-container">
      {isLoading ? (
        <Card>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '300px' }}
          >
            <Loader size="sm" />
          </div>
        </Card>
      ) : (
        <div className="row g-3">
          {/* Left Profile Card */}
          <div className="col-12 col-sm-12 col-md-5 col-lg-4">
            <UserProfileDetailsCard user_details={user_details} navigation={true} />
          </div>
          {/* Right Details Card */}
          <div className="col-12 col-sm-12 col-md-7 col-lg-8">
            <Card className="cp-24 cmb-17">
              <div className="text-14-500-18 color-text-blue cmb-10">
                Profile Details :
              </div>
              <div className="row">
                <div className="col-md-4 col-sm-6 col-12">
                  <DetailItem
                    label="Membership ID"
                    value={user_details?.member_id}
                  />
                </div>
                <div className="col-md-4 col-sm-6 col-12">
                  <DetailItem
                    label="Current Type"
                    value={user_details?.current_plan}
                  />
                </div>
                <div className="col-md-4 col-sm-6 col-12">
                  <DetailItem
                    label="Switch To"
                    value={user_details?.requested_plan}
                  />
                </div>

                <div className="col-md-4 col-sm-6 col-12">
                  <DetailItem
                    label="Join Date"
                    value={moment(user_details?.join_date).format(
                      'DD-MMM-YYYY'
                    )}
                  />
                </div>
                <div className="col-md-4 col-sm-6 col-12">
                  <DetailItem
                    label="Valid Till"
                    value={moment(user_details?.valid_till).format(
                      'DD-MMM-YYYY'
                    )}
                  />
                </div>

                <div className="col-md-4 col-sm-6 col-12">
                  <DetailItem
                    label="CV"
                    value={user_details?.cv}
                    type="url"
                    fileName={user_details?.cv}
                    handleFileDownload={handleFileDownload}
                    filePath={CVPath}
                    isView
                  />
                </div>
                <div className="col-md-4 col-sm-6 col-12">
                  <DetailItem
                    label="Submitted Documents"
                    value={user_details?.payment_proof}
                    type="url"
                    fileName={user_details?.payment_proof}
                    handleFileDownload={handleFileDownload}
                    filePath={paymentAbstractPath}
                    isView={true}
                  />
                </div>
                <div className="col-md-4 col-sm-6 col-12">
                  <DetailItem
                    label="Membership Type Status"
                    value={getStatus(
                      user_details?.membership_status,
                      'request'
                    )}
                    labelClassName={getStatusColor(
                      user_details?.membership_status
                    )}
                  />
                </div>
              </div>
            </Card>

            <Card className="cp-24 cmb-17 cmt-20">
              <div className="text-14-500-18 color-text-blue cmb-10">
                Education Details :
              </div>

              {!(
                educational_details?.ug_course ||
                educational_details?.pg_course ||
                educational_details?.phd_course ||
                educational_details?.disciplines ||
                educational_details?.languages
              ) ? (
                <div className="row">
                  <div className="col-12 text-center text-14-400-18 color-light-grey cpt-20 cpb-20">
                    No educational data found.
                  </div>
                </div>
              ) : (
                <div className="row">
                  {/* UG Section */}
                  <EducationSection
                    labelPrefix="UG"
                    details={{
                      course: educational_details?.ug_course,
                      department: educational_details?.ug_department,
                      university: educational_details?.ug_university,
                      institution: educational_details?.ug_institution,
                      year_of_completion:
                        educational_details?.ug_year_of_completion,
                    }}
                  />

                  {/* PG Section */}
                  <EducationSection
                    labelPrefix="PG"
                    details={{
                      course: educational_details?.pg_course,
                      department: educational_details?.pg_department,
                      university: educational_details?.pg_university,
                      institution: educational_details?.pg_institution,
                      year_of_completion:
                        educational_details?.pg_year_of_completion,
                    }}
                  />

                  {/* Ph.D Section */}

                  <EducationSection
                    labelPrefix="Ph.D"
                    details={{
                      course: educational_details?.phd_course,
                      department: educational_details?.phd_department,
                      university: educational_details?.phd_university,
                      institution: educational_details?.phd_institution,
                      year_of_completion:
                        educational_details?.phd_year_of_completion,
                    }}
                  />
                  {/* Disciplines */}
                  {educational_details?.disciplines && (
                    <>
                      <div className="text-14-500-18 color-text-blue cmt-7">
                        Disciplines :
                      </div>
                      <div className="d-flex flex-wrap gap-2 cmt-16">
                        {educational_details?.disciplines
                          .split(',')
                          .map((disc, index) => (
                            <div
                              key={index}
                              className="cps-12 cpe-12 cpb-6 cpt-6 text-14-400-18 color-black-olive bg-F3F4F8 br-5"
                            >
                              {disc.trim()}
                            </div>
                          ))}
                      </div>
                    </>
                  )}

                  {/* Languages */}
                  {educational_details?.languages && (
                    <>
                      <div className="text-14-500-18 color-text-blue cmt-19">
                        Languages :
                      </div>
                      <div className="d-flex flex-wrap gap-2 cmt-16">
                        {educational_details?.languages
                          .split(',')
                          .map((lang, index) => (
                            <div
                              key={index}
                              className="cps-12 cpe-12 cpb-6 cpt-6 text-14-400-18 color-black-olive bg-F3F4F8 br-5"
                            >
                              {lang.trim()}
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </Card>

            <Card className="cmb-12 cmt-12">
              <div className="text-20-400 color-1717 cpb-13 cps-24 cpe-24 cpt-24">
                Review Membership Type Change Request :
              </div>
              <hr className="m-0 p-0" />
              <div className="cps-24 cpt-18 cpe-24 cpb-18">
                <RequestSection
                  title="Membership Review Details"
                  fields={[
                    { label: 'Name', value: resource_details?.resource_name },
                    {
                      label: 'Id',
                      value: resource_details?.resource_member_id,
                    },
                    {
                      label: 'Email Id',
                      value: resource_details?.resource_email_id,
                    },
                    {
                      label: 'Submitted',
                      value: resource_details?.submitted_at,
                      isDate: true,
                    },
                    {
                      label: 'Submitted Documents',
                      value: user_details?.payment_proof,
                      type: 'url',
                      filePath: paymentAbstractPath,
                      colClass: 'col-12',
                    },

                    {
                      label: 'Comment',
                      value: user_details?.resource_comment || '-',
                      colClass: 'col-12',
                    },
                  ]}
                />

                {/* Admin Section */}
                {isActionTakenByAdmin ? (
                  <RequestSection
                    title="Admin Review Details"
                    fields={[
                      { label: 'Name', value: admin_details?.admin_name },
                      { label: 'Id', value: admin_details?.admin_member_id },
                      {
                        label: 'Email Id',
                        value: admin_details?.admin_email_id,
                      },
                      {
                        label: 'Submitted',
                        value: admin_details?.submitted_at,
                        isDate: true,
                      },
                      {
                        label: 'Submitted Documents',
                        value: admin_details?.admin_document,
                        type: 'url',
                        filePath: requestDataPath,
                      },
                      {
                        label: 'Status',
                        value: getStatus(
                          admin_details?.admin_status,
                          'request'
                        ),
                        labelClassName: getStatusColor(
                          admin_details?.admin_status
                        ),
                      },
                      {
                        label: 'Comment',
                        value: admin_details?.admin_comment || '-',
                        colClass: 'col-12',
                      },
                    ]}
                  />
                ) : (
                  isAdmin && (
                    <ActionForm
                      initialValues={initialValues}
                      isRejecting={isRejecting}
                      handleReject={handleReject}
                      handleApproveRequest={handleApproveRequest}
                      btnLoading={btnLoading}
                      setIsRejecting={setIsRejecting}
                      onHide={() => navigate(-1)}
                    />
                  )
                )}

                {/* Super Admin Section */}
                {isActionTakenBySuperAdmin ? (
                  <RequestSection
                    title="Super Admin Review Details"
                    fields={[
                      { label: 'Name', value: sadmin_details?.sadmin_name },
                      { label: 'Id', value: sadmin_details?.sadmin_member_id },
                      {
                        label: 'Email Id',
                        value: sadmin_details?.sadmin_email_id,
                      },
                      {
                        label: 'Submitted',
                        value: sadmin_details?.submitted_at,
                        isDate: true,
                      },
                      {
                        label: 'Submitted Documents',
                        value: sadmin_details?.sadmin_document,
                        type: 'url',
                        filePath: requestDataPath,
                      },
                      {
                        label: 'Status',
                        value: getStatus(
                          sadmin_details?.sadmin_status,
                          'request'
                        ),
                        labelClassName: getStatusColor(
                          sadmin_details?.sadmin_status
                        ),
                      },
                      {
                        label: 'Comment',
                        value: sadmin_details?.sadmin_comment || '-',
                        colClass: 'col-12',
                      },
                    ]}
                  />
                ) : (
                  isSuperAdmin && (
                    <ActionForm
                      initialValues={initialValues}
                      isRejecting={isRejecting}
                      handleReject={handleReject}
                      handleApproveRequest={handleApproveRequest}
                      btnLoading={btnLoading}
                      setIsRejecting={setIsRejecting}
                      onHide={() => navigate(-1)}
                    />
                  )
                )}
              </div>
            </Card>

            <div className="d-flex justify-content-start gap-2 cmt-16">
              {((user_type === "6" && admin_details?.admin_status === 0) ||
                (user_type === "0" && sadmin_details?.sadmin_status === 0)) &&
                updateAccess === "1" && (
                  <Button
                    icon={
                      <img
                        src={icons.Edit}
                        alt=""
                        style={{
                          filter: "brightness(0) invert(1)",
                          marginRight: "8px",
                        }}
                        height={18}
                        width={18}
                      />
                    }
                    btnStyle="primary-dark"
                    className="cps-15 cpe-15 h-37"
                    text="Edit Member"
                    onClick={() => {
                      dispatch(setIsEditUserRequestProfile(true));
                      dispatch(
                        setIsEditDataOfProfile(requestDetails?.user_details)
                      );
                    }}
                  />
                )}

              <Button
                text="Back"
                btnStyle="light-outline"
                className="cps-20 cpe-20 h-37"
                onClick={() => navigate(-1)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {isOpenConfirmationDialog && (
        <RequestVerificationModal
          handleConfirmation={() => handleVerifyOtp()}
          onHide={() => setIsOpenConfirmationDialog(false)}
          title="You want to share the approve request to super admin"
          isLoading={otpVerification}
        />
      )}

      {/* OTP Modal */}
      {showOtpDialog && (
        <VerifyOtpModel
          title="Membership Type Change Request"
          showApproveDialog={showOtpDialog}
          onHide={() => setShowOtpDialog(false)}
          onVerify={handleVerifyOtp}
          sendOtpRequest={sendOtpRequest}
          otpVerification={otpVerification}
        />
      )}

      {isEditUserRequestProfile && (
        <UpdateUserDetailsPopup requestId={requestId} />
      )}
    </div>
  );
};

export default MembershipTypeChangeDetails;
